import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';
import TableHeader from './TableHeader';
import TableCell from './TableCell';
import { 
  defaultColumnWidth, 
  defaultRowHeight, 
  frozenColumnStyle,
  tableVariants
} from '../constants/MasterSpreadsheetStyles';

const SpreadsheetTable = forwardRef(({
  // Data
  filteredData,
  columnWidths,
  rowHeights,
  visibleColumnNames,
  buttonContainerHeight,
  isButtonContainerExpanded,
  
  // Selection state
  activeCell,
  selectionStart,
  selectionEnd,
  editingCell,
  editingValue,
  
  // Sorting and filtering
  sortColumn,
  sortDirection,
  columnFilters,
  columnFilterOptions,
  
  // Functions
  getVisibleColumns,
  handleSort,
  handleFilterChange,
  handleCellClick,
  handleCellDoubleClick,
  handleEditingChange,
  finishEditing,
  handleEditingKeyDown,
  enterEditMode,
  isCellSelected,
  getCellBorderStyle,
  setEditingValue,
  setEditingCell,
  setSelectionStart,
  setSelectionEnd,
  setActiveCell,
  updateData,
  updateCells,
  
  // Additional props
  currentSheetType,
  year,
  selectRefs,
  isCompanyInClientList,
  setSelectedRequirement,
  setShowCompletionModal,
  replaceOnNextEdit,
  
  // Mouse handlers
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
}, ref) => {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={tableVariants}
      className="flex flex-col flex-1 min-h-0 overflow-hidden bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl"
      onMouseLeave={handleMouseUp}
    >
      <div 
        className="inline-block min-w-full flex-1 min-h-0 overflow-auto scroll-container" 
        tabIndex={-1}
        style={{
          transform: 'translate3d(0, 0, 0)',
          WebkitTransform: 'translate3d(0, 0, 0)',
          willChange: 'scroll-position',
          WebkitOverflowScrolling: 'touch',
          contain: 'paint',
          isolation: 'isolate',
        }}
      >
        <table
          ref={ref}
          className="min-w-max w-full border-collapse select-none focus:outline-none"
          tabIndex={0}
          onMouseDown={(e) => {
            // Focus the table when clicking
            if (ref.current && !ref.current.contains(document.activeElement)) {
              ref.current.focus();
            }
            handleMouseDown(e);
          }}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <TableHeader
            getVisibleColumns={getVisibleColumns}
            visibleColumnNames={visibleColumnNames}
            columnWidths={columnWidths}
            defaultColumnWidth={defaultColumnWidth}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            columnFilters={columnFilters}
            columnFilterOptions={columnFilterOptions}
            handleFilterChange={handleFilterChange}
            selectRefs={selectRefs}
          />
          <tbody>
            {filteredData.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                style={{
                  height: rowHeights[rowIndex] || defaultRowHeight,
                }}
                className={`group ${rowIndex === filteredData.length - 1 ? 'border-b-2' : ''}`}
              >
                <td
                  className={`text-center font-medium cursor-pointer row-header relative ${
                    selectionStart &&
                    selectionEnd &&
                    ((selectionStart[0] <= rowIndex &&
                      selectionEnd[0] >= rowIndex) ||
                      (selectionStart[0] >= rowIndex &&
                        selectionEnd[0] <= rowIndex))
                      ? 'bg-gradient-to-r from-blue-50 to-indigo-50'
                      : ''
                  }`}
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 25,
                    background: rowIndex % 2 === 0 
                      ? '#ffffff' 
                      : '#fafbfc',
                    backgroundColor: rowIndex % 2 === 0 
                      ? '#ffffff' 
                      : '#fafbfc',
                    borderBottom: '1px solid rgba(226,232,240,0.5)',
                    transform: 'translate3d(0, 0, 0)',
                    willChange: 'transform',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    isolation: 'isolate',
                  }}
                >
                  <div className="p-3 text-sm text-slate-600 font-semibold">
                    {rowIndex + 1}
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-1 cursor-row-resize row-resize-handle 
                    opacity-0 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                </td>
                {Object.keys(getVisibleColumns()).map((key, colIndex) => (
                  <td
                    key={`${rowIndex}-${key}`}
                    className={`
                      relative p-4 
                      ${activeCell &&
                      activeCell[0] === rowIndex &&
                      activeCell[1] === colIndex
                        ? 'ring-3 ring-blue-500/80 ring-inset bg-gradient-to-br from-blue-200/70 to-blue-300/70 shadow-xl z-10'
                        : isCellSelected(rowIndex, colIndex)
                        ? 'bg-gradient-to-br from-slate-100/90 to-slate-200/90 shadow-md ring-1 ring-slate-300/60'
                        : ''}
                      ${key === 'Company Name' ? 'frozen-column-cell font-medium text-slate-700' : 'text-slate-600'}
                      transition-none
                    `}
                    style={{
                      ...(key === 'Company Name' ? {
                        ...frozenColumnStyle,
                        background: (activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex)
                          ? '#93c5fd'
                          : isCellSelected(rowIndex, colIndex) 
                          ? '#94a3b8'
                          : (rowIndex % 2 === 0 
                              ? '#ffffff' 
                              : '#fafbfc'),
                        backgroundColor: (activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex)
                          ? '#93c5fd'
                          : isCellSelected(rowIndex, colIndex) 
                          ? '#94a3b8'
                          : (rowIndex % 2 === 0 
                              ? '#ffffff' 
                              : '#fafbfc'),
                        opacity: 1,
                        zIndex: 30,
                        isolation: 'isolate',
                        ...((activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex) && {
                          boxShadow: 'inset 0 0 0 2px rgba(59, 130, 246, 0.8), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                          zIndex: 30,
                        }),
                        ...(isCellSelected(rowIndex, colIndex) && !(activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex) && {
                          boxShadow: 'inset 0 0 0 2px rgba(100, 116, 139, 0.6), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                          zIndex: 25,
                        }),
                      } : {
                        background: (activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex)
                          ? 'linear-gradient(135deg, rgba(147,197,253,0.7) 0%, rgba(147,197,253,0.8) 100%)'
                          : isCellSelected(rowIndex, colIndex)
                          ? 'linear-gradient(135deg, rgba(148,163,184,0.9) 0%, rgba(148,163,184,0.9) 100%)'
                          : (rowIndex % 2 === 0 
                              ? 'rgba(255,255,255,0.5)' 
                              : 'rgba(248,250,252,0.5)'),
                        ...((activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex) && {
                          boxShadow: 'inset 0 0 0 1px rgba(59, 130, 246, 0.5), 0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        }),
                        ...(isCellSelected(rowIndex, colIndex) && !(activeCell && activeCell[0] === rowIndex && activeCell[1] === colIndex) && {
                          boxShadow: 'inset 0 0 0 1px rgba(100, 116, 139, 0.4), 0 2px 4px -1px rgba(0, 0, 0, 0.1)',
                        }),
                      }),
                      width:
                        columnWidths[key] !== undefined
                          ? columnWidths[key]
                          : defaultColumnWidth,
                      maxWidth:
                        columnWidths[key] !== undefined
                          ? columnWidths[key]
                          : defaultColumnWidth,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      cursor:
                        editingCell &&
                        editingCell[0] === rowIndex &&
                        editingCell[1] === colIndex
                          ? 'text'
                          : 'pointer',
                      borderBottom: '1px solid rgba(226,232,240,0.5)',
                      ...getCellBorderStyle(rowIndex, colIndex),
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCellClick(rowIndex, colIndex);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      handleCellDoubleClick(rowIndex, colIndex);
                    }}
                  >
                    <TableCell
                      columnKey={key}
                      value={row[key]}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      rowData={row}
                      isEditing={editingCell && editingCell[0] === rowIndex && editingCell[1] === colIndex}
                      editingValue={editingValue}
                      setEditingValue={setEditingValue}
                      handleEditingChange={handleEditingChange}
                      finishEditing={finishEditing}
                      handleEditingKeyDown={handleEditingKeyDown}
                      enterEditMode={enterEditMode}
                      currentSheetType={currentSheetType}
                      year={year}
                      updateCells={updateCells}
                      updateData={updateData}
                      activeCell={activeCell}
                      setEditingCell={setEditingCell}
                      setSelectedRequirement={setSelectedRequirement}
                      setShowCompletionModal={setShowCompletionModal}
                      isCompanyInClientList={isCompanyInClientList}
                      replaceOnNextEdit={replaceOnNextEdit}
                      setSelectionStart={setSelectionStart}
                      setSelectionEnd={setSelectionEnd}
                      setActiveCell={setActiveCell}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Spacer to ensure last rows are visible above controls */}
        <div 
          style={{ 
            height: isButtonContainerExpanded ? `${buttonContainerHeight + 40}px` : '100px',
            minHeight: isButtonContainerExpanded ? `${buttonContainerHeight + 40}px` : '100px',
            flexShrink: 0
          }} 
        />
      </div>
    </motion.div>
  );
});

SpreadsheetTable.displayName = 'SpreadsheetTable';

export default SpreadsheetTable;